<template>
  <div class="edit-user-page py-3">
    <header class="user-toolbar">
      <div class="user-toolbar__title">
        <span class="user-toolbar__accent" aria-hidden="true"></span>
        <div>
          <div class="text-uppercase small text-muted">User Settings</div>
          <h1>Edit User #{{ $route.params.id }}</h1>
        </div>
      </div>
      <div class="user-toolbar__actions">
        <CButton size="sm" class="back-btn" @click="goBack">Back</CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="user-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="user-alert">{{ successMessage }}</CAlert>

    <div v-if="isLoading" class="user-loading-card">
      <CSpinner size="sm" class="mr-2" />
      <span>Loading user…</span>
    </div>

    <form v-else class="user-form" @submit.prevent="update">
      <!-- Access section -->
      <section class="user-section">
        <header class="user-section__head">
          <div class="user-section__heading">
            <span class="user-section__step">1</span>
            <div>
              <h2>Access</h2>
              <p>Account, organisation, department and role</p>
            </div>
          </div>
        </header>

        <div class="user-section__body">
          <div class="form-grid">
            <div class="form-field">
              <label class="form-label" for="edit-name">Name</label>
              <input id="edit-name" v-model.trim="form.name" type="text" class="form-input" />
            </div>
            <div class="form-field">
              <label class="form-label" for="edit-email">Email</label>
              <input id="edit-email" v-model.trim="form.email" type="email" class="form-input" />
            </div>

            <div class="form-field">
              <label class="form-label">Role</label>
              <AppDropdown v-model="form.role" :options="roleDropdownOptions" placeholder="Select role" searchable />
            </div>
            <div class="form-field">
              <label class="form-label">Organisation</label>
              <AppDropdown v-model="form.organisationId" :options="organisationOptions" searchable
                search-placeholder="Search organisation…"
                :placeholder="isLoadingOrganisations ? 'Loading organisations…' : 'Select organisation'" />
            </div>
            <div class="form-field form-field--span2">
              <label class="form-label">Department</label>
              <AppDropdown v-model="form.departmentId" :options="departmentOptions" searchable
                search-placeholder="Search department…" :placeholder="departmentPlaceholder" />
            </div>
            <div class="form-field">
              <label class="form-label">State</label>
              <AppDropdown v-model="form.stateId" :options="stateDropdownOptions" searchable
                search-placeholder="Search state…"
                :placeholder="isLoadingStates ? 'Loading states…' : 'Select state'" />
            </div>
            <div class="form-field">
              <label class="form-label">Branch</label>
              <AppDropdown v-model="form.branchId" :options="branchDropdownOptions" searchable
                search-placeholder="Search branch…"
                :placeholder="branchPlaceholder" />
            </div>
          </div>
        </div>
      </section>

      <!-- Personal details -->
      <section class="user-section">
        <header class="user-section__head">
          <div class="user-section__heading">
            <span class="user-section__step">2</span>
            <div>
              <h2>Personal Details</h2>
              <p>Contact information and basic personal data</p>
            </div>
          </div>
        </header>
        <div class="user-section__body">
          <div class="form-grid form-grid--triple">
            <div class="form-field">
              <label class="form-label" for="edit-phone">Phone</label>
              <input id="edit-phone" v-model.trim="form.phone" type="tel" class="form-input" placeholder="+91 98xxxxxxxx" />
            </div>
            <div class="form-field">
              <label class="form-label" for="edit-alt-phone">Alternate phone</label>
              <input id="edit-alt-phone" v-model.trim="form.alternate_phone" type="tel" class="form-input" />
            </div>
            <div class="form-field">
              <label class="form-label">Date of birth</label>
              <SingleDatePicker v-model="form.date_of_birth" />
            </div>
            <div class="form-field">
              <label class="form-label">Gender</label>
              <AppDropdown v-model="form.gender" :options="genderOptions" placeholder="Select gender" />
            </div>
            <div class="form-field">
              <label class="form-label">Blood group</label>
              <AppDropdown v-model="form.blood_group" :options="bloodGroupOptions" placeholder="Select" />
            </div>
            <div class="form-field">
              <label class="form-label">Marital status</label>
              <AppDropdown v-model="form.marital_status" :options="maritalOptions" placeholder="Select" />
            </div>
          </div>
        </div>
      </section>

      <!-- Address -->
      <section class="user-section">
        <header class="user-section__head">
          <div class="user-section__heading">
            <span class="user-section__step">3</span>
            <div>
              <h2>Address</h2>
              <p>Residential / mailing address</p>
            </div>
          </div>
        </header>
        <div class="user-section__body">
          <div class="form-grid form-grid--single">
            <div class="form-field">
              <label class="form-label" for="edit-address">Street address</label>
              <textarea id="edit-address" v-model="form.address" rows="3" class="form-input form-textarea"
                placeholder="House / building, street, locality"></textarea>
            </div>
            <div class="form-grid form-grid--quad">
              <div class="form-field">
                <label class="form-label" for="edit-city">City</label>
                <input id="edit-city" v-model.trim="form.city" type="text" class="form-input" />
              </div>
              <div class="form-field">
                <label class="form-label" for="edit-state">State</label>
                <input id="edit-state" v-model.trim="form.state" type="text" class="form-input" />
              </div>
              <div class="form-field">
                <label class="form-label" for="edit-pincode">Pincode</label>
                <input id="edit-pincode" v-model.trim="form.pincode" type="text" inputmode="numeric" maxlength="10"
                  class="form-input" />
              </div>
              <div class="form-field">
                <label class="form-label" for="edit-country">Country</label>
                <input id="edit-country" v-model.trim="form.country" type="text" class="form-input" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Emergency contact -->
      <section class="user-section">
        <header class="user-section__head">
          <div class="user-section__heading">
            <span class="user-section__step">4</span>
            <div>
              <h2>Emergency Contact</h2>
              <p>Whom to reach in case of an emergency</p>
            </div>
          </div>
        </header>
        <div class="user-section__body">
          <div class="form-grid form-grid--triple">
            <div class="form-field">
              <label class="form-label" for="edit-ec-name">Contact name</label>
              <input id="edit-ec-name" v-model.trim="form.emergency_contact_name" type="text" class="form-input" />
            </div>
            <div class="form-field">
              <label class="form-label" for="edit-ec-phone">Contact phone</label>
              <input id="edit-ec-phone" v-model.trim="form.emergency_contact_phone" type="tel" class="form-input" />
            </div>
            <div class="form-field">
              <label class="form-label">Relation</label>
              <AppDropdown v-model="form.emergency_contact_relation" :options="relationOptions"
                placeholder="Select relation" searchable />
            </div>
          </div>
        </div>
      </section>

      <!-- Employment -->
      <section class="user-section">
        <header class="user-section__head">
          <div class="user-section__heading">
            <span class="user-section__step">5</span>
            <div>
              <h2>Employment</h2>
              <p>HR record for this staff member</p>
            </div>
          </div>
        </header>
        <div class="user-section__body">
          <div class="form-grid form-grid--triple">
            <div class="form-field">
              <label class="form-label" for="edit-emp-code">Employee code</label>
              <input id="edit-emp-code" v-model.trim="form.employee_code" type="text" class="form-input" />
            </div>
            <div class="form-field">
              <label class="form-label" for="edit-biometric">Biometric ID</label>
              <input id="edit-biometric" v-model.trim="form.biometric_id" type="text" class="form-input"
                placeholder="Fingerprint PIN on the device, e.g. 15" />
            </div>
            <div class="form-field">
              <label class="form-label" for="edit-designation">Designation</label>
              <input id="edit-designation" v-model.trim="form.designation" type="text" class="form-input" />
            </div>
            <div class="form-field">
              <label class="form-label" for="edit-doj">Date of joining</label>
              <input id="edit-doj" v-model="form.date_of_joining" type="date" class="form-input" />
            </div>
            <div class="form-field">
              <label class="form-label">Employment type</label>
              <AppDropdown v-model="form.employment_type" :options="employmentTypeOptions" placeholder="Select" />
            </div>
            <div class="form-field">
              <label class="form-label">Employment status</label>
              <AppDropdown v-model="form.employment_status" :options="employmentStatusOptions" placeholder="Select" />
            </div>
          </div>
        </div>
      </section>

      <!-- KYC & Bank -->
      <section class="user-section">
        <header class="user-section__head">
          <div class="user-section__heading">
            <span class="user-section__step">6</span>
            <div>
              <h2>KYC &amp; Bank</h2>
              <p>Identity proof and payroll account details</p>
            </div>
          </div>
          <span class="user-hint-pill">Sensitive</span>
        </header>
        <div class="user-section__body">
          <div class="form-grid">
            <div class="form-field">
              <label class="form-label" for="edit-pan">PAN number</label>
              <input id="edit-pan" v-model.trim="form.pan_number" type="text" class="form-input"
                maxlength="10" style="text-transform: uppercase" placeholder="ABCDE1234F" />
            </div>
            <div class="form-field">
              <label class="form-label" for="edit-aadhaar">Aadhaar number</label>
              <input id="edit-aadhaar" v-model.trim="form.aadhaar_number" type="text" inputmode="numeric"
                maxlength="14" class="form-input" placeholder="12-digit" />
            </div>
            <div class="form-field">
              <label class="form-label" for="edit-bank-acc">Bank account number</label>
              <input id="edit-bank-acc" v-model.trim="form.bank_account_number" type="text" class="form-input" />
            </div>
            <div class="form-field">
              <label class="form-label" for="edit-bank-name">Bank name</label>
              <input id="edit-bank-name" v-model.trim="form.bank_name" type="text" class="form-input" />
            </div>
            <div class="form-field">
              <label class="form-label" for="edit-bank-ifsc">IFSC</label>
              <input id="edit-bank-ifsc" v-model.trim="form.bank_ifsc" type="text" class="form-input"
                maxlength="11" style="text-transform: uppercase" placeholder="SBIN0001234" />
            </div>
          </div>
        </div>
      </section>

      <!-- Admin notes -->
      <section class="user-section">
        <header class="user-section__head">
          <div class="user-section__heading">
            <span class="user-section__step">7</span>
            <div>
              <h2>Admin Notes</h2>
              <p>Internal notes visible to administrators</p>
            </div>
          </div>
        </header>
        <div class="user-section__body">
          <div class="form-grid form-grid--single">
            <div class="form-field">
              <textarea v-model="form.notes" rows="3" class="form-input form-textarea"
                placeholder="Any additional notes about this staff member"></textarea>
            </div>
          </div>
        </div>
      </section>

      <footer class="user-form__footer">
        <span class="form-meta">Changes apply to user #{{ $route.params.id }}</span>
        <div class="user-form__actions">
          <CButton type="button" size="sm" class="back-btn" :disabled="isSaving" @click="goBack">Cancel</CButton>
          <CButton type="submit" size="sm" class="submit-btn" :disabled="isSaving">
            {{ isSaving ? 'Saving…' : 'Save Changes' }}
          </CButton>
        </div>
      </footer>
    </form>
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import SingleDatePicker from '@/components/SingleDatePicker.vue'
import statesApi from '@/api/states.js'
import branchesApi from '@/api/branches.js'
import {
  buildUserDetailForm,
  buildUserUpdatePayload,
  getCachedUserById,
  normalizeUserDetailResponse,
  normalizeUsersResponse,
  setUserCache,
  upsertCachedUser,
  ROLE_OPTIONS
} from './userHelpers'
import { getStoredRoles } from '@/auth/access'

const GENDER_OPTIONS = ['Male', 'Female', 'Other', 'Prefer not to say']
const MARITAL_OPTIONS = ['Single', 'Married', 'Divorced', 'Widowed', 'Separated']
const BLOOD_GROUP_OPTIONS = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-']
const EMPLOYMENT_TYPE_OPTIONS = ['full_time', 'part_time', 'contract', 'intern', 'consultant']
const EMPLOYMENT_STATUS_OPTIONS = ['active', 'on_leave', 'probation', 'suspended', 'resigned', 'terminated']
const RELATION_OPTIONS = ['Father', 'Mother', 'Spouse', 'Sibling', 'Child', 'Guardian', 'Friend', 'Other']

function emptyForm () {
  return {
    name: '',
    email: '',
    role: 'None',
    organisation: '',
    department: '',
    organisationId: null,
    departmentId: null,
    stateId: null,
    branchId: null,
    phone: '',
    alternate_phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    country: '',
    date_of_birth: '',
    gender: '',
    blood_group: '',
    marital_status: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relation: '',
    employee_code: '',
    biometric_id: '',
    designation: '',
    date_of_joining: '',
    employment_type: '',
    employment_status: '',
    pan_number: '',
    aadhaar_number: '',
    bank_account_number: '',
    bank_name: '',
    bank_ifsc: '',
    notes: ''
  }
}

export default {
  name: 'EditUser',
  components: { AppDropdown, SingleDatePicker },
  data () {
    return {
      isLoadingUser: false,
      isSaving: false,
      errorMessage: '',
      successMessage: '',
      form: emptyForm(),
      roleOptions: ROLE_OPTIONS,
      organisations: [],
      departments: [],
      states: [],
      branches: [],
      isLoadingOrganisations: false,
      isLoadingDepartments: false,
      isLoadingStates: false,
      isLoadingBranches: false,
      suppressDepartmentReset: false,
      // Hydration guard — keeps the org-change watcher from wiping the
      // user's stateId/branchId before they've been loaded from the API.
      suppressBranchReset: false,
      genderOptions: GENDER_OPTIONS.map((v) => ({ value: v, label: v })),
      maritalOptions: MARITAL_OPTIONS.map((v) => ({ value: v, label: v })),
      bloodGroupOptions: BLOOD_GROUP_OPTIONS.map((v) => ({ value: v, label: v })),
      employmentTypeOptions: EMPLOYMENT_TYPE_OPTIONS.map((v) => ({ value: v, label: v.replace(/_/g, ' ') })),
      employmentStatusOptions: EMPLOYMENT_STATUS_OPTIONS.map((v) => ({ value: v, label: v.replace(/_/g, ' ') })),
      relationOptions: RELATION_OPTIONS.map((v) => ({ value: v, label: v }))
    }
  },
  computed: {
    isLoading () {
      return this.isLoadingUser || this.isLoadingOrganisations
    },
    organisationOptions () {
      return this.organisations.map((org) => ({ value: org.id, label: org.name }))
    },
    departmentOptions () {
      return [
        { value: null, label: 'No department' },
        ...this.departments.map((dept) => ({ value: dept.id, label: dept.name }))
      ]
    },
    departmentPlaceholder () {
      if (!this.form.organisationId) return 'Pick an organisation first'
      if (this.isLoadingDepartments) return 'Loading departments…'
      return 'Select department'
    },
    stateDropdownOptions () {
      return [
        { value: null, label: 'No state' },
        ...this.states.map((s) => ({ value: s.id, label: s.name }))
      ]
    },
    branchDropdownOptions () {
      // Cascade off State (both are global). With no state picked, all
      // branches show — picking a branch then auto-fills its state.
      const filtered = this.form.stateId
        ? this.branches.filter((b) => Number(b.state_id) === Number(this.form.stateId))
        : this.branches
      return [
        { value: null, label: 'No branch' },
        ...filtered.map((b) => ({ value: b.id, label: b.name }))
      ]
    },
    branchPlaceholder () {
      if (this.isLoadingBranches) return 'Loading branches…'
      if (!this.branches.length) return 'No branches in the system'
      if (this.form.stateId && !this.branchDropdownOptions.find((o) => o.value !== null)) return 'No branches under this state'
      return 'Select branch'
    },
    // hr_manager (and only hr_manager — admins/superadmins are unrestricted) may
    // only ever assign the tele sales role. The backend enforces the same rule in
    // UserController::update; this just keeps the dropdown honest.
    isTelesalesOnlyEditor () {
      const roles = getStoredRoles().map((r) => String(r).toLowerCase())
      if (roles.some((r) => ['admin', 'superadmin'].includes(r))) return false
      return roles.includes('hr_manager')
    },
    roleDropdownOptions () {
      if (this.isTelesalesOnlyEditor) {
        return [{ value: 'telesales', label: 'tele sales' }]
      }
      return ROLE_OPTIONS.map((role) => ({ value: role, label: role.replace(/_/g, ' ') }))
    }
  },
  watch: {
    'form.organisationId' (newId, oldId) {
      if (newId === oldId) return
      if (this.organisations.length) {
        const org = this.organisations.find((o) => o.id === newId)
        this.form.organisation = org ? org.name : ''
      }
      if (!this.suppressDepartmentReset) {
        this.form.departmentId = null
      }
      this.departments = []
      if (newId) this.loadDepartments(newId)

      // State + Branch are GLOBAL — independent of org. The org switcher
      // does NOT touch them.
    },
    'form.departmentId' (newId) {
      if (!this.departments.length) return
      const dept = this.departments.find((d) => d.id === newId)
      this.form.department = dept ? dept.name : ''
    },
    'form.stateId' (newId, oldId) {
      if (newId === oldId || this.suppressBranchReset) return
      // If the picked branch no longer belongs to the new state, clear it.
      if (!this.form.branchId) return
      const branch = this.branches.find((b) => Number(b.id) === Number(this.form.branchId))
      if (branch && Number(branch.state_id) !== Number(newId)) {
        this.form.branchId = null
      }
    },
    'form.branchId' (newId) {
      // Auto-fill stateId from the selected branch (branch carries state).
      if (!newId || !this.branches.length) return
      const branch = this.branches.find((b) => Number(b.id) === Number(newId))
      if (branch && Number(branch.state_id) !== Number(this.form.stateId)) {
        this.suppressBranchReset = true
        this.form.stateId = Number(branch.state_id)
        this.$nextTick(() => { this.suppressBranchReset = false })
      }
    }
  },
  created () {
    this.loadOrganisations()
    this.loadStates()
    this.loadBranches()
    this.loadUser()
  },
  methods: {
    goBack () {
      this.$router.go(-1)
    },
    loadUser () {
      this.isLoadingUser = true
      this.errorMessage = ''
      this.successMessage = ''

      const applyUser = (user) => {
        const normalizedUser = normalizeUserDetailResponse(user)
        const detail = buildUserDetailForm(normalizedUser)
        this.suppressDepartmentReset = true
        this.suppressBranchReset = true
        this.form = { ...this.form, ...detail }
        this.$nextTick(() => {
          this.suppressDepartmentReset = false
          this.suppressBranchReset = false
        })
        if (detail.organisationId) {
          this.loadDepartments(detail.organisationId)
        }
      }

      const loadFromListFallback = () => {
        const cachedUser = getCachedUserById(this.$route.params.id)
        if (cachedUser) {
          applyUser(cachedUser)
          return Promise.resolve()
        }
        return axios.get(`${this.$apiAdress}/api/users?token=${localStorage.getItem('api_token')}`)
          .then((response) => {
            const normalized = normalizeUsersResponse(response.data)
            setUserCache(normalized.users)
            const user = normalized.users.find((item) => String(item.id) === String(this.$route.params.id))
            if (user) {
              applyUser(user)
              return
            }
            this.errorMessage = 'Unable to locate the requested user.'
          })
      }

      axios.get(`${this.$apiAdress}/api/users/${this.$route.params.id}?token=${localStorage.getItem('api_token')}`)
        .then((response) => {
          const user = normalizeUserDetailResponse(response.data)
          if (Object.keys(user).length) {
            applyUser(user)
            return
          }
          return loadFromListFallback()
        })
        .catch((error) => {
          console.log(error)
          return loadFromListFallback().catch((fallbackError) => {
            console.log(fallbackError)
            this.errorMessage = error?.response?.data?.message || 'Unable to load the user right now.'
          })
        })
        .finally(() => {
          this.isLoadingUser = false
        })
    },
    loadOrganisations () {
      this.isLoadingOrganisations = true
      const token = localStorage.getItem('api_token')
      axios.get(`${this.$apiAdress}/api/organisations?token=${token}`)
        .then((response) => {
          const data = response.data
          if (Array.isArray(data)) this.organisations = data
          else if (Array.isArray(data?.data)) this.organisations = data.data
          else this.organisations = []
          if (this.form.organisationId) {
            const org = this.organisations.find((o) => o.id === this.form.organisationId)
            if (org) this.form.organisation = org.name
          }
        })
        .catch((error) => { console.log(error) })
        .finally(() => { this.isLoadingOrganisations = false })
    },
    loadDepartments (organisationId) {
      this.isLoadingDepartments = true
      const token = localStorage.getItem('api_token')
      axios.get(`${this.$apiAdress}/api/department?token=${token}&organisation_id=${organisationId}`)
        .then((response) => {
          const data = response.data
          if (Array.isArray(data)) this.departments = data
          else if (Array.isArray(data?.data)) this.departments = data.data
          else this.departments = []
          const dept = this.departments.find((d) => d.id === this.form.departmentId)
          this.form.department = dept ? dept.name : ''
        })
        .catch((error) => { console.log(error) })
        .finally(() => { this.isLoadingDepartments = false })
    },
    loadStates () {
      this.isLoadingStates = true
      statesApi.list(this.$apiAdress)
        .then((data) => {
          this.states = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : [])
        })
        .catch((error) => { console.log(error) })
        .finally(() => { this.isLoadingStates = false })
    },
    loadBranches () {
      // Branches are GLOBAL — no org filter. List returns every branch.
      this.isLoadingBranches = true
      branchesApi.list(this.$apiAdress)
        .then((data) => {
          this.branches = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : [])
          // Once branches arrive, derive stateId from the user's branch
          // record if we have a branchId but no stateId.
          if (this.form.branchId && !this.form.stateId) {
            const branch = this.branches.find((b) => Number(b.id) === Number(this.form.branchId))
            if (branch) {
              this.suppressBranchReset = true
              this.form.stateId = Number(branch.state_id)
              this.$nextTick(() => { this.suppressBranchReset = false })
            }
          }
        })
        .catch((error) => { console.log(error) })
        .finally(() => { this.isLoadingBranches = false })
    },
    update () {
      this.isSaving = true
      this.errorMessage = ''
      this.successMessage = ''

      axios.post(
        `${this.$apiAdress}/api/users/${this.$route.params.id}?token=${localStorage.getItem('api_token')}`,
        buildUserUpdatePayload(this.form)
      )
        .then(() => {
          this.successMessage = 'Successfully updated user.'
          upsertCachedUser(this.$route.params.id, {
            name: this.form.name,
            email: this.form.email,
            role: this.form.role,
            organisation: this.form.organisation,
            department: this.form.department,
            organisationId: this.form.organisationId,
            departmentId: this.form.departmentId,
            branchId: this.form.branchId,
            stateId: this.form.stateId
          })
        })
        .catch((error) => {
          console.log(error)
          const data = error?.response?.data
          if (data?.errors) {
            const first = Object.values(data.errors)[0]
            this.errorMessage = Array.isArray(first) ? first[0] : String(first)
          } else {
            this.errorMessage = data?.message || 'Unable to save user changes right now.'
          }
        })
        .finally(() => {
          this.isSaving = false
        })
    }
  }
}
</script>

<style scoped>
.edit-user-page {
  padding: 0.25rem 0 1.25rem;
}

.user-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.user-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.user-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.user-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.user-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.user-toolbar__actions >>> .btn.back-btn,
.user-form__actions >>> .btn.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.user-toolbar__actions >>> .btn.back-btn:hover:not(:disabled),
.user-form__actions >>> .btn.back-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.user-form__actions >>> .btn.submit-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 36px;
  padding: 0 1.1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.user-form__actions >>> .btn.submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
  color: #ffffff;
}

.user-form__actions >>> .btn.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  filter: saturate(0.6);
}

.user-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

.user-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
  overflow: hidden;
}

.user-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
}

.user-section__heading {
  display: flex;
  align-items: center;
  gap: 0.7rem;
}

.user-section__step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #ffffff;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22);
  flex-shrink: 0;
}

.user-section__heading h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  line-height: 1.2;
}

.user-section__heading p {
  margin: 0;
  font-size: 0.74rem;
  color: #6b7280;
}

.user-hint-pill {
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
  background: #f1f5f9;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
}

.user-section__body {
  padding: 1.1rem 1rem;
}

.user-loading-card {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 3rem 1rem;
  color: #6b7280;
  font-size: 0.85rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem 1rem;
}

.form-grid--single {
  grid-template-columns: 1fr;
}

.form-grid--triple {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.form-grid--quad {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.form-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.form-field--span2 {
  grid-column: span 2;
}

.form-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.form-input {
  width: 100%;
  height: 38px;
  padding: 0.3rem 0.7rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.85rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  appearance: none;
  -webkit-appearance: none;
}

.form-textarea {
  height: auto;
  min-height: 80px;
  padding: 0.55rem 0.7rem;
  resize: vertical;
  line-height: 1.5;
}

.form-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.user-form__footer {
  position: sticky;
  bottom: 0.5rem;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.85rem 1rem;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 -4px 12px rgba(15, 23, 42, 0.06), 0 2px 6px rgba(15, 23, 42, 0.04);
}

.form-meta {
  font-size: 0.78rem;
  color: #6b7280;
}

.user-form__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

@media (max-width: 991px) {
  .form-grid--quad { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 767px) {
  .form-grid--triple { grid-template-columns: 1fr 1fr; }
}

@media (max-width: 575px) {
  .form-grid,
  .form-grid--triple,
  .form-grid--quad {
    grid-template-columns: 1fr;
  }
  .form-field--span2 { grid-column: span 1; }
  .user-form__footer { flex-direction: column; align-items: stretch; }
  .user-form__actions { justify-content: flex-end; }
}
</style>
